import { useState, useEffect } from 'react';
import { Card } from '../../../../components/ui/Card';
import { Button } from '../../../../components/ui/Button';
import { Switch } from '../../../../components/ui/Switch';
import { Building, Plus, Trash2, AlertTriangle, Loader2, Info, WifiOff } from 'lucide-react';
import { settingsApi } from '../../../../services/api/endpoints/settings';
import { usePermissions } from '../../../../hooks/usePermissions';
import type { TaxIntegrationConfig, TaxRate, DRCTaxType } from '../../../../types/settings';
import toast from 'react-hot-toast';

const TAX_TYPES: { value: DRCTaxType; label: string }[] = [
  { value: 'TVA',   label: 'TVA (Taxe sur la Valeur Ajoutée — 16%)' },
  { value: 'IBP',   label: 'IBP (Impôt sur les Bénéfices des PME)' },
  { value: 'IPR',   label: 'IPR (Impôt Professionnel sur les Revenus)' },
  { value: 'QP',    label: 'Quôte-part à l’État' },
  { value: 'OTHER', label: 'Autre' },
];

const DEFAULT_CONFIG: TaxIntegrationConfig = {
  dgiEnabled: false,
  taxpayerNumber: '',
  autoCalculateTax: false,
  declarationFrequency: 'monthly',
  taxRates: [],
};

const BLANK_RATE: TaxRate = { type: 'TVA', rate: 16, description: '', accountId: '' };

const CACHE_KEY = 'compta-tax-settings';

export function TaxIntegration() {
  const { isAdmin, can } = usePermissions();
  const canManage = can('manage_integrations') || isAdmin;

  const [config, setConfig] = useState<TaxIntegrationConfig>(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      return cached ? { ...DEFAULT_CONFIG, ...JSON.parse(cached) } : DEFAULT_CONFIG;
    } catch { return DEFAULT_CONFIG; }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await settingsApi.getTaxIntegrationSettings();
        if (!res.success || !res.data) throw new Error();
        const fresh = { ...DEFAULT_CONFIG, ...res.data };
        setConfig(fresh);
        setIsOffline(false);
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(fresh)); } catch {}
      } catch {
        setIsOffline(true);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  const save = async () => {
    if (!canManage) return;
    setIsSaving(true);
    setError(null);
    try {
      const res = await settingsApi.updateIntegrations({ taxIntegration: config });
      if (!res.success) throw new Error();
      toast.success('Configuration fiscale enregistrée');
    } catch {
      setError('Impossible de sauvegarder. Veuillez réessayer.');
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const set = <K extends keyof TaxIntegrationConfig>(key: K, value: TaxIntegrationConfig[K]) =>
    setConfig(prev => ({ ...prev, [key]: value }));

  const addRate = () => setConfig(prev => ({ ...prev, taxRates: [...prev.taxRates, { ...BLANK_RATE }] }));
  const removeRate = (idx: number) => setConfig(prev => ({ ...prev, taxRates: prev.taxRates.filter((_, i) => i !== idx) }));
  const updateRate = <K extends keyof TaxRate>(idx: number, key: K, value: TaxRate[K]) =>
    setConfig(prev => ({ ...prev, taxRates: prev.taxRates.map((r, i) => i === idx ? { ...r, [key]: value } : r) }));

  if (isLoading) {
    return (
      <Card title="Intégration DGI (Impôts)" icon={Building}>
        <div className="flex items-center justify-center h-40 gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Chargement...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Intégration DGI (Impôts)" icon={Building}>
      <div className="space-y-5">
        {error && (
          <div className="flex items-start gap-3 p-3 rounded-lg border bg-destructive/5 border-destructive/20 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          Configurez l’intégration avec la Direction Générale des Impôts (DGI) pour le calcul et la déclaration automatiques.
        </p>

        {/* DGI enable */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Activer l’intégration DGI</p>
            <p className="text-xs text-muted-foreground">Synchronisation avec la plateforme fiscale officielle</p>
          </div>
          <Switch checked={config.dgiEnabled} onCheckedChange={v => set('dgiEnabled', v)} disabled={!canManage || isSaving} />
        </div>

        {/* NIF — info only, shared with EInvoice */}
        <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/40 text-xs text-muted-foreground">
          <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-primary" />
          <span>Le Numéro d'Identification Fiscale (NIF) est commun à toutes les intégrations DGI. Configurez-le dans la section <strong className="text-foreground">Facturation électronique (DGI)</strong> ci-dessus.</span>
        </div>

        {/* Toggles row */}
        <div className="grid grid-cols-2 gap-4 border-t border-border pt-4">
          <div className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-muted/50">
            <p className="text-sm font-medium text-foreground">Calcul automatique</p>
            <Switch checked={config.autoCalculateTax} onCheckedChange={v => set('autoCalculateTax', v)} disabled={!canManage || isSaving} />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">Fréquence de déclaration</label>
            <select
              className="w-full text-sm border border-border rounded-md px-2 py-1.5 bg-background text-foreground"
              value={config.declarationFrequency}
              onChange={e => set('declarationFrequency', e.target.value as any)}
              disabled={!canManage || isSaving}
            >
              <option value="monthly">Mensuelle</option>
              <option value="quarterly">Trimestrielle</option>
              <option value="annual">Annuelle</option>
            </select>
          </div>
        </div>

        {/* Tax rates */}
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-foreground">Taux d’imposition applicables</p>
            {canManage && (
              <Button variant="ghost" size="sm" icon={Plus} onClick={addRate} disabled={isSaving}>
                Ajouter
              </Button>
            )}
          </div>
          {config.taxRates.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">Aucun taux configuré.</p>
          ) : (
            <div className="space-y-2">
              {config.taxRates.map((r, idx) => (
                <div key={idx} className="grid grid-cols-[1.2fr_0.5fr_1.5fr_0.8fr_auto] gap-2 items-center">
                  <select
                    className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground"
                    value={r.type}
                    onChange={e => updateRate(idx, 'type', e.target.value as DRCTaxType)}
                    disabled={!canManage || isSaving}
                  >
                    {TAX_TYPES.map(t => <option key={t.value} value={t.value}>{t.value}</option>)}
                  </select>
                  <input
                    type="number"
                    className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground"
                    placeholder="%"
                    value={r.rate}
                    onChange={e => updateRate(idx, 'rate', Number(e.target.value))}
                    disabled={!canManage || isSaving}
                  />
                  <input
                    className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground"
                    placeholder="Description"
                    value={r.description}
                    onChange={e => updateRate(idx, 'description', e.target.value)}
                    disabled={!canManage || isSaving}
                  />
                  <input
                    className="text-xs border border-border rounded px-2 py-1 bg-background text-foreground"
                    placeholder="Compte (ex: 445600)"
                    value={r.accountId ?? ''}
                    onChange={e => updateRate(idx, 'accountId', e.target.value)}
                    disabled={!canManage || isSaving}
                  />
                  {canManage && (
                    <button onClick={() => removeRate(idx)} className="text-destructive hover:text-destructive/70">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {canManage && (
          <div className="flex justify-end pt-2 border-t border-border">
            <Button onClick={save} disabled={isSaving}>
              {isSaving ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Enregistrement...</> : 'Enregistrer'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}